import React from 'react';
import { Container } from '@material-ui/core';
import Chart from 'react-apexcharts';
import EcoIcon from '@material-ui/icons/Eco';
import ShowChartIcon from '@material-ui/icons/ShowChart';
import BarChartIcon from '@material-ui/icons/BarChart';
import DateRangeIcon from '@material-ui/icons/DateRange';
import GetAppIcon from '@material-ui/icons/GetApp';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import sarForestImg from '../../assets/images/sar_forest_satellite.jpg';
import sarDeforImg from '../../assets/images/sar_deforestation_after.png';
import { useStyles, AwaiLogic } from './awai.module';
import Footer from '../footer/Footer';

const TABS = [
  { key: 'vision', label: 'Visión General' },
  { key: 'alertas', label: 'Alertas Críticas' },
  { key: 'metodologia', label: 'Metodología' },
];

const Awai = () => {
  const classes = useStyles();
  const {
    activeTab,
    setActiveTab,
    sliderPos,
    imageCompareRef,
    startDrag,
    stopDrag,
    onMouseMove,
    onTouchMove,
    monthlyBarSeries,
    monthlyBarOptions,
    accumulatedSeries,
    accumulatedOptions,
    selectedYears,
    setSelectedYears,
    yearRangeLabel,
    availableYears,
    totalHa,
    chartMode,
    chartModeLabel,
    chartHeight,
    annualSummary,
    metricsDescription,
    sentinelYear,
    setSentinelYear,
    sentinelDeforImg,
    SENTINEL_YEARS,
  } = AwaiLogic();

  return (
    <div className={classes.root}>
      {/* ── Top Nav ──────────────────────────────────────────────────── */}
      <nav className={classes.topNav}>
        <div className={classes.topNavTabs}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`${classes.topNavTab} ${
                activeTab === tab.key ? classes.topNavTabActive : ''
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* ── Body ─────────────────────────────────────────────────────── */}
      <div className={classes.body}>
        {/* ── Sidebar ──────────────────────────────────────────────── */}
        <aside className={classes.sidebar}>
          <button
            className={`${classes.sidebarBtn} ${
              activeTab === 'vision' ? classes.sidebarBtnActive : ''
            }`}
            onClick={() => setActiveTab('vision')}
            title="Visión General"
          >
            <EcoIcon style={{ fontSize: 18 }} />
          </button>
          <button
            className={`${classes.sidebarBtn} ${
              activeTab === 'alertas' ? classes.sidebarBtnActive : ''
            }`}
            onClick={() => setActiveTab('alertas')}
            title="Alertas Críticas"
          >
            <ShowChartIcon style={{ fontSize: 18 }} />
          </button>
          <button
            className={`${classes.sidebarBtn} ${
              activeTab === 'metodologia' ? classes.sidebarBtnActive : ''
            }`}
            onClick={() => setActiveTab('metodologia')}
            title="Metodología"
          >
            <BarChartIcon style={{ fontSize: 18 }} />
          </button>
          <div className={classes.sidebarSpacer} />
          <button className={classes.sidebarBottomBtn} title="Ayuda">
            <HelpOutlineIcon style={{ fontSize: 18 }} />
          </button>
        </aside>

        {/* ── Main ─────────────────────────────────────────────────── */}
        <main className={classes.main}>
          {activeTab === 'vision' && (
            <>
              {/* ── Sentinel Section ───────────────────────────────── */}
              <Container maxWidth="lg">
                <div className={classes.sentinelSection}>
                  {/* Title row */}
                  <div className={classes.sentinelTitleRow}>
                    <div>
                      <div className={classes.sentinelLabel}>
                        Sentinel-1 Data Feed
                      </div>
                      <h2 className={classes.sentinelTitle}>
                        Máscara de Deforestación Sentinel-1
                      </h2>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        flexWrap: 'wrap',
                      }}
                    >
                      {SENTINEL_YEARS.map((yr) => (
                        <button
                          key={yr}
                          onClick={() => setSentinelYear(yr)}
                          style={{
                            background:
                              sentinelYear === yr ? '#06E89F22' : '#0a1628',
                            border: `1px solid ${
                              sentinelYear === yr ? '#06E89F' : '#1a3050'
                            }`,
                            borderRadius: '4px',
                            color: sentinelYear === yr ? '#06E89F' : '#6b8aaa',
                            fontFamily: 'MontserratSemiBold',
                            fontSize: '11px',
                            padding: '4px 10px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                        >
                          {yr}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Info panel + image comparison */}
                  <div className={classes.sentinelContent}>
                    {/* Left info panel */}
                    <div className={classes.sentinelInfoPanel}>
                      <div className={classes.infoPanelLabel}>
                        Satellite Precision
                      </div>
                      <p className={classes.infoPanelText}>
                        Análisis de Cobertura: Esta vista utiliza radar de
                        apertura sintética (SAR) de Sentinel-1 para penetrar la
                        nubosidad y detectar cambios en el bosque. El comparador
                        permite identificar la progresión exacta de la pérdida
                        de dosel forestal entre ciclos.
                      </p>
                    </div>

                    {/* Image comparison slider */}
                    <div
                      ref={imageCompareRef}
                      className={classes.imageCompare}
                      onMouseMove={onMouseMove}
                      onMouseUp={stopDrag}
                      onMouseLeave={stopDrag}
                      onTouchMove={onTouchMove}
                      onTouchEnd={stopDrag}
                    >
                      {/* Base: SAR clean forest (right side / before 2018) */}
                      <div
                        className={classes.imageBase}
                        style={{ backgroundImage: `url(${sarForestImg})` }}
                      />

                      {/* Overlay: máscara deforestación del año seleccionado */}
                      <div
                        className={classes.imageOverlay}
                        style={{
                          backgroundImage: `url(${sentinelDeforImg})`,
                          clipPath: `inset(0 ${100 - sliderPos}% 0 0)`,
                        }}
                      />

                      {/* Slider line */}
                      <div
                        className={classes.sliderLine}
                        style={{ left: `${sliderPos}%` }}
                      />

                      {/* Slider handle */}
                      <div
                        className={classes.sliderHandle}
                        style={{ left: `${sliderPos}%` }}
                        onMouseDown={startDrag}
                        onTouchStart={startDrag}
                      >
                        <span
                          style={{
                            fontSize: '11px',
                            color: '#9ab',
                            fontWeight: 700,
                          }}
                        >
                          ⟺
                        </span>
                      </div>

                      {/* Legend */}
                      <div className={classes.imageLegend}>
                        <div className={classes.legendItem}>
                          <div
                            className={classes.legendDot}
                            style={{ backgroundColor: '#aacd65' }}
                          />
                          Bosque Primario
                        </div>
                        <div className={classes.legendItem}>
                          <div
                            className={classes.legendDot}
                            style={{ backgroundColor: '#E53935' }}
                          />
                          Deforestación
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Container>

              {/* ── Monthly Analytics ──────────────────────────────── */}
              <Container maxWidth="lg">
                <div className={classes.analyticsSection}>
                  <div className={classes.sectionLabel}>Monthly Analytics</div>

                  <div className={classes.sectionTitleRow}>
                    <h2 className={classes.sectionTitle}>
                      Tendencias de
                      <br />
                      Deforestación Mensual
                    </h2>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        flexWrap: 'wrap',
                      }}
                    >
                      {availableYears.map((yr) => (
                        <button
                          key={yr}
                          onClick={() =>
                            setSelectedYears((prev) =>
                              prev.includes(yr)
                                ? prev.length > 1
                                  ? prev.filter((y) => y !== yr)
                                  : prev
                                : [...prev, yr].sort()
                            )
                          }
                          style={{
                            background: selectedYears.includes(yr)
                              ? '#06E89F22'
                              : '#0a1628',
                            border: `1px solid ${
                              selectedYears.includes(yr) ? '#06E89F' : '#1a3050'
                            }`,
                            borderRadius: '4px',
                            color: selectedYears.includes(yr)
                              ? '#06E89F'
                              : '#6b8aaa',
                            fontFamily: 'MontserratSemiBold',
                            fontSize: '11px',
                            padding: '4px 8px',
                            cursor: 'pointer',
                          }}
                        >
                          {yr}
                        </button>
                      ))}
                      <div
                        className={classes.dateRangePicker}
                        style={{ marginLeft: '4px' }}
                      >
                        <span className={classes.dateRangeYear}>
                          {yearRangeLabel}
                        </span>
                        <DateRangeIcon
                          style={{ fontSize: 14, color: '#4a6a8a' }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={classes.chartModeBadge}>
                    Vista: {chartModeLabel}
                    {chartMode === 'annual'}
                  </div>

                  <div className={classes.chartRow}>
                    <div className={classes.chartBox}>
                      <Chart
                        options={monthlyBarOptions}
                        series={monthlyBarSeries}
                        type="bar"
                        height={chartHeight}
                      />

                      {chartMode === 'annual' && annualSummary && (
                        <div className={classes.annualSummaryTable}>
                          <div className={classes.annualSummaryHeader}>
                            <span>Año</span>
                            <span>Total (ha)</span>
                            <span>Mes pico</span>
                            <span>Pico (ha)</span>
                          </div>
                          {annualSummary.map((row) => (
                            <div
                              key={row.year}
                              className={classes.annualSummaryRow}
                            >
                              <span>{row.year}</span>
                              <span>{row.total.toFixed(2)}</span>
                              <span>{row.peakMonth}</span>
                              <span>{row.peakValue.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Metrics side panel */}
                    <div className={classes.metricsPanel}>
                      <div className={classes.metricsPanelLabel}>
                        Métricas de Alerta
                      </div>
                      <p className={classes.metricsPanelText}>
                        {metricsDescription}
                      </p>
                    </div>
                  </div>
                </div>

                {/* ── Accumulated Deforestation ──────────────────────── */}
                <div className={classes.accumulatedSection}>
                  <div className={classes.sectionLabel}>Impact Report</div>
                  <h2 className={classes.sectionTitle}>
                    Deforestación Acumulada
                  </h2>

                  <div className={classes.accumulatedRow}>
                    {/* Left info panel */}
                    <div className={classes.accumulatedInfoPanel}>
                      <div className={classes.infoPanelLabel}>
                        Impacto Neto Consolidado
                      </div>
                      <p className={classes.infoPanelText}>
                        Representación de la pérdida total acumulada. La curva
                        ascendente cuantifica el cambio de estructura del bosque
                        a largo plazo, proporcionando una base de datos para el
                        reporte y verificación de la  mitigación de carbono del
                        proyecto.
                      </p>
                    </div>

                    {/* Area chart */}
                    <div className={classes.accumulatedChart}>
                      <Chart
                        options={accumulatedOptions}
                        series={accumulatedSeries}
                        type="area"
                        height={280}
                      />
                    </div>

                    {/* Stats card */}
                    <div className={classes.statsCard}>
                      <div>
                        <TrendingUpIcon className={classes.statsIcon} />
                        <div className={classes.statsLabel}>
                          Área Forestal Neta Perdida
                        </div>
                        <div className={classes.statsValue}>
                          {Number(totalHa).toLocaleString('es-CL', {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                        <div className={classes.statsUnit}>
                          hectáreas acumuladas
                        </div>
                      </div>

                      {/*<div className={classes.statsDivider}>
                        <div className={classes.exportLabel}>
                          Exportar Reporte
                        </div>
                        <div className={classes.exportRow}>
                          <div>
                            <div className={classes.exportFormat}>
                              PDF / CSV
                            </div>
                            <div className={classes.exportFormatSub}>
                              GeoJson
                            </div>
                          </div>
                          <button
                            className={classes.downloadBtn}
                            title="Descargar"
                          >
                            <GetAppIcon style={{ fontSize: 16 }} />
                          </button>
                        </div>
                      </div>*/}
                    </div>
                  </div>
                </div>
              </Container>
            </>
          )}

          {activeTab === 'alertas' && (
            <Container maxWidth="lg">
              <div className={classes.emptyTab}>
                <div className={classes.sectionLabel}>Sistema de Alertas</div>
                <h2 className={classes.sectionTitle}>Alertas Críticas</h2>
                <p className={classes.emptyTabText}>
                  No hay alertas críticas activas en este momento.
                </p>
              </div>
            </Container>
          )}

          {activeTab === 'metodologia' && (
            <Container maxWidth="lg">
              <div className={classes.emptyTab}>
                <div className={classes.sectionLabel}>Documentación</div>
                <h2 className={classes.sectionTitle}>Metodología</h2>
                <p className={classes.emptyTabText}>
                  El sistema utiliza datos del satélite Sentinel-1 de la Agencia
                  Espacial Europea (ESA) para generar máscaras de detección de
                  deforestación mediante algoritmos de inteligencia artificial.
                  Los datos son procesados cada 6 días con una resolución
                  espacial de 10 metros.
                </p>
              </div>
            </Container>
          )}
          <Footer />
        </main>
      </div>
    </div>
  );
};

export default Awai;
